import { CloseRounded } from "@mui/icons-material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { AgGridTable } from "components/tables/AgGridTable";
import CommonLayout from "layouts/DashboardLayout";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { formatTimestampToDate } from "utilities/common";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useDebounce } from "hooks/useDebounce";
import defaultProductImage from "../../../assets/images/placeholderproduct.svg";
import { capitalizeText } from "utilities/common";
import SearchIcon from "common/customIcons/search";
import EditIcon from "common/customIcons/edit";
import StatusText from "common/component/StatusText";
import {
  getAllBlazeCategory,
  getAllProducts,
  syncProductAdmin,
} from "../../../redux/slices/hyperwolf/products";
import { fetchBrands } from "../../../redux/slices/hyperwolfBrand";
import { withStyles } from "@mui/styles";
import { getLastSyncDate } from "../../../redux/slices/hyperwolf/auth";
import { setData } from "utilities/common";
import { getData } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";

// Allow empty or numeric (including decimals) for price fields
const isPriceValue = (v) => v === "" || /^\d*\.?\d*$/.test(v);

const SELL_TYPES = ["Retail", "Wholesale", "Both"];

function getSearchQueryFromParams(searchParams) {
  return (searchParams.get("search") || "").trim();
}

function parseFiltersFromSearchParams(searchParams) {
  const categories = searchParams.get("categories");
  const brands = searchParams.get("brands");
  const sellTypeRaw = searchParams.get("sellType") || "Both";
  const sellType = SELL_TYPES.includes(sellTypeRaw) ? sellTypeRaw : "Both";
  return {
    categoryIds: categories ? categories.split(",").filter(Boolean) : [],
    brandIds: brands ? brands.split(",").filter(Boolean) : [],
    sellType,
    priceMin: searchParams.get("priceMin") || "",
    priceMax: searchParams.get("priceMax") || "",
    status: searchParams.get("status") === "Inactive" ? "Inactive" : "Active",
  };
}

function hasFilterParamsInUrl(searchParams) {
  return !!(
    searchParams.get("categories") ||
    searchParams.get("brands") ||
    searchParams.get("sellType") ||
    searchParams.get("priceMin") ||
    searchParams.get("priceMax") ||
    searchParams.get("status")
  );
}

function buildProductListSearchParams(searchTerm, f) {
  const p = new URLSearchParams();
  const t = String(searchTerm ?? "").trim();
  if (t) p.set("search", t);
  if (f.categoryIds?.length) p.set("categories", f.categoryIds.join(","));
  if (f.brandIds?.length) p.set("brands", f.brandIds.join(","));
  if (f.sellType && f.sellType !== "Both") p.set("sellType", f.sellType);
  if (f.priceMin) p.set("priceMin", String(f.priceMin));
  if (f.priceMax) p.set("priceMax", String(f.priceMax));
  if (f.status === "Inactive") p.set("status", "Inactive");
  return p;
}

function filtersToPayload(f) {
  const statusActive =
    f.status === "Active" ? true : f.status === "Inactive" ? false : undefined;
  return {
    categoryId:
      f.categoryIds?.length > 0 ? f.categoryIds.join(",") : undefined,
    brandId: f.brandIds?.length > 0 ? f.brandIds.join(",") : undefined,
    sellType: f.sellType || undefined,
    minPrice: f.priceMin ? Number(f.priceMin) : undefined,
    maxPrice: f.priceMax ? Number(f.priceMax) : undefined,
    active: statusActive,
  };
}

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

export default function HyperwolfProducts() {
  const dispatch = useDispatch();
  const {
    loading = false,
    products = {},
    syncProductLoader = false,
  } = useSelector((state) => state.hyperwolfProducts);
  const { lastSyncDate } = useSelector((state) => state.hyperwolfAuth);
  const pageRef = useRef(0);
  const mainGridApiRef = useRef("");
  const gridRefApi = useRef(null);
  const sortRef = useRef({ sortBy: "", order: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingOwnUrlUpdateRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState(() => getSearchQueryFromParams(searchParams));
  const searchTermRef = useRef(searchTerm);
  const [filters, setFilters] = useState(() => parseFiltersFromSearchParams(searchParams));
  const productFiltersPayload = useRef(
    hasFilterParamsInUrl(searchParams)
      ? filtersToPayload(parseFiltersFromSearchParams(searchParams))
      : {}
  );
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  const [waitingTime, setWaitingTime] = useState("");
  const [isActiveTab, setIsActiveTab] = useState(true);
  const [priceError, setPriceError] = useState("");
  const navigate = useNavigate();

  const syncListToUrl = useCallback(
    (term, f) => {
      pendingOwnUrlUpdateRef.current = true;
      setSearchParams(buildProductListSearchParams(term, f), { replace: true });
    },
    [setSearchParams]
  );

  const { blazeCategories = [] } = useSelector(
    (state) => state.hyperwolfProducts
  );
  const { brandsList: hyperwolfBrandsList = [] } = useSelector(
    (state) => state.hyperwolfBrand ?? {}
  );
  const categoriesList = Array.isArray(blazeCategories?.values)
    ? blazeCategories.values
    : Array.isArray(blazeCategories)
    ? blazeCategories
    : [];
  const brandsList = Array.isArray(hyperwolfBrandsList)
    ? hyperwolfBrandsList
    : [];

  let waitingTimeInterval = useRef(null);

  let localUser = getData("login-user-info");
  if (localUser) {
    localUser = JSON.parse(localUser);
  }

  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Product", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Product", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Product", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Product", "Delete");

  const handleSearch = useDebounce(() => {
    // Perform search operation with the debounced term
    pageRef.current = 0;
    searchFn();
  }, 500);

  const onSyncBlazeProducts = () => {
    if (!syncProductLoader && !waitingTime) {
      dispatch(syncProductAdmin()).then(() => {
        setData("adminLastSync", Date.now());
        setWaitingTime("Wait for 2 min");
        runWaitingTimeInterval();
      });
    }
  };

  const runWaitingTimeInterval = () => {
    if (getData("adminLastSync")) {
      const value = JSON.parse(getData("adminLastSync"));
      waitingTimeInterval.current = setInterval(() => {
        const diffInMin = Math.floor(((Date.now() - value) / 1000 / 60) << 0);
        if (diffInMin > 2) {
          setWaitingTime("");
          clearInterval(waitingTimeInterval.current);
        }
        let waitingMsg = "";
        if (diffInMin === 0 || diffInMin === 1) waitingMsg = "Wait for 2 min";
        if (diffInMin === 2) waitingMsg = "Wait for 1 min";
        setWaitingTime(waitingMsg);
      }, 2000);
    }
  };

  const columnDefs = [
    {
      headerName: "Product/SKU",
      field: "name",
      suppressMenu: true,
      wrapText: true,
      autoHeight: true,
      cellRenderer: (props) => {
        return (
          <Tooltip title={capitalizeText(props?.data?.name)} arrow placement="top">
            <Box display="flex" alignItems="center" sx={{ cursor: "pointer" }}>
              <Avatar
                alt="Product"
                src={props?.data?.assets?.[0]?.mediumURL || defaultProductImage}
                sx={{ width: 32, height: 32 }}
              />
              <MDBox textAlign="left" lineHeight={1.75} ml={1.25} mt={0.125}>
                <Typography
                  variant="h6"
                  color="dark"
                  lineHeight={1.25}
                  fontWeight="semibold"
                  className="text-14 truncate line-1"
                >
                  {capitalizeText(props?.data?.name)}
                </Typography>
                <Typography className="text-12" variant="p" color="secondary" lineHeight={1.25}>
                  {props?.data?.sku ? `SKU: ${props?.data?.sku}` : "-"}
                </Typography>
              </MDBox>
            </Box>
          </Tooltip>
        );
      },
      minWidth: 260,
    },
    {
      headerName: "Category",
      field: "categoryName",
      suppressMenu: false,
      minWidth: 140,
      valueGetter: (params) =>
         params.data?.categoryName || "-",
    },
    {
      headerName: "Brand",
      field: "brandName",
      suppressMenu: false,
      minWidth: 140,
      valueGetter: (params) => params.data?.brandName || "-",
    },
    {
      headerName: "Distributor",
      field: "vendorName",
      suppressMenu: false,
      minWidth: 160,
      valueGetter: (params) => params.data?.vendorName || "-",
    },
    {
      headerName: "Retail Price",
      field: "unitPrice",
      suppressMenu: true,
      minWidth: 110,
      cellRenderer: (props) => {
        const val = props?.data?.unitPrice ?? props?.value;
        if (val == null && val !== 0) return "-";
        return (
          <Typography variant="body2" fontWeight="medium" lineHeight={1}>
            ${Number(val) || "0"}
          </Typography>
        );
      },
      cellStyle: { textAlign: "right" },
    },
    // {
    //   headerName: "Sell Type",
    //   field: "sellType",
    //   suppressMenu: false,
    //   minWidth: 100,
    //   valueGetter: (params) =>
    //     params.data?.sellType ? capitalizeText(String(params.data.sellType)) : "Retail",
    // },
    // {
    //   headerName: "Custom Gram Type",
    //   field: "customGramType",
    //   suppressMenu: false,
    //   minWidth: 140,
    //   valueGetter: (params) => {
    //     const val =
    //       params.data?.productData?.customGramType ??
    //       params.data?.customGramType ??
    //       "";
    //     return val || "-";
    //   },
    // },
    {
      headerName: "Status",
      field: "active",
      suppressMenu: false,
      minWidth: 100,
      cellRenderer: (props) => {
        const raw = props?.data?.active ?? props?.data?.status;
        const isActive =
          raw === true ||
          raw === "true" ||
          (typeof raw === "string" && raw.toLowerCase() === "active");
        const statusLabel = isActive ? "active" : "inactive";
        return <StatusText status={statusLabel} />;
      },
    },
    {
      headerName: "Actions",
      suppressMenu: true,
      cellRenderer: (props) => {
        const id = props?.data?.id ?? props?.data?._id;
        if (!id) return null;
        return (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/hyperwolf/products/edit/${id}`, {
                state: {
                  productsListSearch: searchTermRef.current,
                  productsListQuery: buildProductListSearchParams(
                    searchTermRef.current,
                    filters
                  ).toString(),
                },
              });
            }}
          >
            <EditIcon width="16px" height="16px" />
          </IconButton>
        );
      },
      cellStyle: { textAlign: "right", justifyContent: "end" },
      headerClass: "right-aligned-cell-header",
      minWidth: 90,
      pinned: "right",
    },
  ];

  const getRows = useCallback(
    async (params) => {
      gridRefApi.current = params;
      const filterPayload = productFiltersPayload?.current || {};
      const sortModel = params?.sortModel?.[0];
      const nextSortBy = sortModel?.colId || "";
      const nextOrder = (sortModel?.sort || "").toLowerCase();
      if (nextSortBy !== sortRef.current.sortBy || nextOrder !== sortRef.current.order) {
        sortRef.current = { sortBy: nextSortBy, order: nextOrder };
        pageRef.current = 0;
      }
      const activeVal =
        filterPayload.active === undefined || filterPayload.active === ""
          ? undefined
          : filterPayload.active === true || filterPayload.active === "true";
      const payload = {
        skip: pageRef.current,
        limit: 20,
        userType: "admin",
      };
      if (searchTermRef.current != null && searchTermRef.current !== "")
        payload.searchTerm = searchTermRef.current;
      if (filterPayload.categoryId != null && filterPayload.categoryId !== "")
        payload.categoryId = filterPayload.categoryId;
      if (filterPayload.brandId != null && filterPayload.brandId !== "")
        payload.brandId = filterPayload.brandId;
      if (activeVal !== undefined) payload.active = activeVal;
      if (filterPayload.minPrice != null && filterPayload.minPrice !== "")
        payload.minPrice = filterPayload.minPrice;
      if (filterPayload.maxPrice != null && filterPayload.maxPrice !== "")
        payload.maxPrice = filterPayload.maxPrice;
      if (filterPayload.sellType != null && filterPayload.sellType !== "")
        payload.sellType = filterPayload.sellType;
      payload.sortBy = nextSortBy || "name";
      if (nextOrder === "asc" || nextOrder === "desc") payload.order = nextOrder;
      dispatch(getAllProducts(payload))
        .unwrap()
        .then((res) => {
          const { values, total } = res ?? {};
          const productsArray = (values || []).map((item) => ({
            ...item,
            id: item._id,
            createdDate: formatTimestampToDate(item?.created),
          }));
          params.successCallback(productsArray, total ?? 0);
          pageRef.current = pageRef.current + 20;
        })
        .catch(() => {
          params.successCallback([], 0);
        });
    },
    [dispatch]
  );

  const searchFn = () => {
    const dataSource = {
      getRows,
    };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };

  const searchParamsKey = searchParams.toString();
  useEffect(() => {
    if (pendingOwnUrlUpdateRef.current) {
      pendingOwnUrlUpdateRef.current = false;
      return;
    }
    const urlSearch = getSearchQueryFromParams(searchParams);
    const urlFilters = parseFiltersFromSearchParams(searchParams);
    const urlFiltersJson = JSON.stringify(urlFilters);
    const filtersJson = JSON.stringify(filters);
    let changed = false;
    if (urlSearch !== searchTerm) {
      searchTermRef.current = urlSearch;
      setSearchTerm(urlSearch);
      changed = true;
    }
    if (urlFiltersJson !== filtersJson) {
      setFilters(urlFilters);
      productFiltersPayload.current = hasFilterParamsInUrl(searchParams)
        ? filtersToPayload(urlFilters)
        : {};
      changed = true;
    }
    if (changed) {
      pageRef.current = 0;
      searchFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate list from URL (back/forward); compare uses render snapshot
  }, [searchParamsKey]);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    syncListToUrl(searchValue, filters);
    handleSearch(searchValue);
  };

  const applyProductFilter = (nextFilters) => {
    const f = nextFilters ?? filters;
    productFiltersPayload.current = filtersToPayload(f);
    pageRef.current = 0;
    syncListToUrl(searchTermRef.current, f);
    const dataSource = { getRows };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", null);
      requestAnimationFrame(() => {
        if (mainGridApiRef.current) {
          mainGridApiRef.current.setGridOption("datasource", dataSource);
        }
      });
    }
  };

  /** Same 500ms delay as search — avoids a network request on every price keystroke */
  const debouncedApplyPriceFilter = useDebounce(() => {
    const { priceMin, priceMax } = filtersRef.current;
    if (priceMin !== "" && priceMax !== "" && +priceMin > +priceMax) return;
    applyProductFilter(filtersRef.current);
  }, 500);

  const clearProductFilters = () => {
    const cleared = {
      categoryIds: [],
      brandIds: [],
      sellType: "Both",
      priceMin: "",
      priceMax: "",
      status: "Active",
    };
    setPriceError("");
    setFilters(cleared);
    productFiltersPayload.current = {};
    pageRef.current = 0;
    syncListToUrl(searchTermRef.current, cleared);
    const dataSource = { getRows };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", null);
      requestAnimationFrame(() => {
        if (mainGridApiRef.current) {
          mainGridApiRef.current.setGridOption("datasource", dataSource);
        }
      });
    }
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (products?.total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [products, loading]);

  useEffect(() => {
    dispatch(getLastSyncDate());
  }, []);

  useEffect(() => {
    dispatch(getAllBlazeCategory({ limit: 200 }));
    dispatch(fetchBrands({ skip: 0, limit: 500, status: "Active" }));
  }, [dispatch]);

  let dt = (lastSyncDate && new Date(lastSyncDate)) || "";
  let la = dt.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName === "Actions") return;
    const id = row?.data?.id ?? row?.data?._id;
    if (id) {
      navigate(`/hyperwolf/products/details/${id}?filter=products`, {
        state: {
          productsListSearch: searchTermRef.current,
          productsListQuery: buildProductListSearchParams(
            searchTermRef.current,
            filters
          ).toString(),
        },
      });
    }
  };

  return (
    <CommonLayout>
      
      <Box sx={{ mt: 2 }}>
          <div className="last-sync">
            <div>Last Sync : {la || "Syncing Now"}</div>
            {canEdit && (
              <LightTooltip
                TransitionComponent={Zoom}
                title={`${
                  syncProductLoader
                    ? "Syncing is in Progress"
                    : waitingTime
                    ? waitingTime + " for next sync"
                    : "Sync products from blaze"
                }`}
              >
                <div
                  className={`product-sync-div ${waitingTime ? "disabled-text" : ""}`}
                  onClick={onSyncBlazeProducts}
                >
                  {syncProductLoader ? (
                    <CircularProgress size={18} />
                  ) : waitingTime ? (
                    waitingTime
                  ) : (
                    "Sync Products"
                  )}
                </div>
              </LightTooltip>
            )}
          </div>
        </Box>
      
      <Box p={{ xs: 2, sm: 3, lg: 3 }} pb={{ lg: 2 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
          <Grid item xs={12} sm={2}>
            <Typography variant="h4">Products</Typography>
          </Grid>
          <Grid item xs={12} sm={10}>
            <Stack
              direction="row"
              justifyContent={{ xs: "start", sm: "end" }}
              flexWrap="wrap"
              alignItems="stretch"
              gap={2}
            >
              <TextField
                className="custom-search-field"
                placeholder="Search"
                label=""
                disabled={loading}
                value={searchTerm}
                onChange={(e) => searchHandler(e.target.value)}
                sx={{ minWidth: "280px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small">
                        <SearchIcon width="18px" height="18px" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchTerm?.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => searchHandler("")} size="small">
                          <CloseRounded />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
              {canCreate && (
                <Link to="/hyperwolf/products/add" style={{ textDecoration: "none" }}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddRoundedIcon />}
                    sx={{ minWidth: "140px" }}
                  >
                    Add Product
                  </MDButton>
                </Link>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Filters: same height/width, gray field bg, compact size */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          {(() => {
            const fieldHeight = 34;
            const fieldWidth = 88;
            const fieldBg = "#eeeeee";
            const fieldSx = {
              width: fieldWidth,
              minWidth: fieldWidth,
              "& .MuiOutlinedInput-root": {
                height: fieldHeight,
                minHeight: fieldHeight,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: `${fieldBg} !important`,
                "& fieldset": {
                  border: "none",
                  borderRadius: 2,
                  "& legend": { display: "none" },
                },
                "&.Mui-focused fieldset": { border: "none" },
                "&:hover": { backgroundColor: fieldBg },
                "&.Mui-focused": { backgroundColor: fieldBg },
                "& .MuiSelect-select, & .MuiInputBase-input": {
                  backgroundColor: `${fieldBg} !important`,
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
              },
              "& .MuiSelect-select, & .MuiInputBase-input": {
                py: 0,
                height: fieldHeight - 2,
                boxSizing: "border-box",
                fontSize: "0.8125rem",
                textAlign: "center",
                paddingLeft: 8,
                paddingRight: 8,
                color: "#1a1a1a",
              },
              "& .MuiOutlinedInput-input": {
                textAlign: "center",
                color: "#1a1a1a !important",
                WebkitTextFillColor: "#1a1a1a !important",
                lineHeight: 1.5,
                caretColor: "#1a1a1a",
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: 1,
                color: "#555555",
              },
              "& .MuiInputBase-root": {
                backgroundColor: `${fieldBg} !important`,
              },
              "& .MuiSelect-icon": {
                color: "text.secondary",
              },
            };
            return (
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} sm={6} md="auto">
                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                      Category
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{
                        ...fieldSx,
                        width: 200,
                        minWidth: 200,
                      }}
                    >
                      <Select
                        multiple
                        displayEmpty
                        value={filters.categoryIds}
                        onChange={(e) => {
                          const val = e.target.value;
                          const next = {
                            ...filters,
                            categoryIds: typeof val === "string" ? (val ? [val] : []) : val || [],
                          };
                          setFilters(next);
                          applyProductFilter(next);
                        }}
                        IconComponent={ExpandMoreIcon}
                        renderValue={(selected) => {
                          if (!selected?.length) return "All";
                          if (selected.length === 1) {
                            const cat = categoriesList.find(
                              (c) => (c.id || c._id || c.categoryId) === selected[0]
                            );
                            return cat?.name || cat?.categoryName || selected[0];
                          }
                          return `${selected.length} selected`;
                        }}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 320 } },
                        }}
                      >
                        {categoriesList.map((cat) => {
                          const id = cat.id || cat._id || cat.categoryId;
                          return (
                            <MenuItem key={id} value={id} dense>
                              <Checkbox
                                size="small"
                                checked={filters.categoryIds.indexOf(id) > -1}
                                sx={{ py: 0, mr: 1 }}
                              />
                              <ListItemText
                                primary={cat?.name || cat?.categoryName || "-"}
                                primaryTypographyProps={{ variant: "body2" }}
                              />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md="auto">
                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                      Brand
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{
                        ...fieldSx,
                        width: 260,
                        minWidth: 260,
                      }}
                    >
                      <Select
                        multiple
                        displayEmpty
                        value={filters.brandIds}
                        onChange={(e) => {
                          const val = e.target.value;
                          const next = {
                            ...filters,
                            brandIds: typeof val === "string" ? (val ? [val] : []) : val || [],
                          };
                          setFilters(next);
                          applyProductFilter(next);
                        }}
                        IconComponent={ExpandMoreIcon}
                        renderValue={(selected) => {
                          if (!selected?.length) return "All";
                          if (selected.length === 1) {
                            const b = brandsList.find((x) => (x.brandId || x._id) === selected[0]);
                            return b?.brandName || b?.name || selected[0];
                          }
                          return `${selected.length} selected`;
                        }}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 320 } },
                        }}
                      >
                        {brandsList.map((brand) => {
                          const id = brand.brandId || brand._id;
                          return (
                            <MenuItem key={id} value={id} dense>
                              <Checkbox
                                size="small"
                                checked={filters.brandIds.indexOf(id) > -1}
                                sx={{ py: 0, mr: 1 }}
                              />
                              <ListItemText
                                primary={brand?.brandName || brand?.name || "-"}
                                primaryTypographyProps={{ variant: "body2" }}
                              />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
                {/* <Grid item xs={12} sm={6} md="auto">
                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                      Sell Type
                    </Typography>
                    <FormControl size="small" sx={fieldSx}>
                      <Select
                        value={filters.sellType}
                        onChange={(e) => {
                          const next = { ...filters, sellType: e.target.value };
                          setFilters(next);
                          applyProductFilter(next);
                        }}
                        IconComponent={ExpandMoreIcon}
                      >
                        <MenuItem value="Both">All</MenuItem>
                        <MenuItem value="Retail">Retail</MenuItem>
                        <MenuItem value="Wholesale">Wholesale</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid> */}
                <Grid item xs={12} sm={6} md="auto">
                  <Box>
                    <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                        Price ($)
                      </Typography>
                      <Box
                        sx={{
                          position: "relative",
                          width: fieldWidth,
                          minWidth: fieldWidth,
                          height: fieldHeight,
                          borderRadius: 2,
                          overflow: "hidden",
                          backgroundColor: fieldBg,
                          border: priceError ? "1px solid #d32f2f" : "none",
                        }}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label="Min price"
                          value={filters.priceMin}
                          onChange={(e) => {
                            if (e.target.value !== "" && !isPriceValue(e.target.value)) return;
                            const next = { ...filters, priceMin: e.target.value };
                            const minVal = e.target.value;
                            const maxVal = filters.priceMax;
                            if (minVal !== "" && maxVal !== "" && +minVal > +maxVal) {
                              setPriceError("Min price cannot be greater than max price");
                            } else {
                              setPriceError("");
                            }
                            setFilters(next);
                            filtersRef.current = next;
                            debouncedApplyPriceFilter();
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            margin: 0,
                            padding: "0 8px",
                            border: "none",
                            outline: "none",
                            backgroundColor: "transparent",
                            color: "#1a1a1a",
                            fontSize: "0.8125rem",
                            textAlign: "center",
                            boxSizing: "border-box",
                          }}
                        />
                        {!filters.priceMin && (
                          <Typography
                            component="span"
                            sx={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              pointerEvents: "none",
                              color: "#555555",
                              fontSize: "0.8125rem",
                            }}
                          >
                            Min
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          position: "relative",
                          width: fieldWidth,
                          minWidth: fieldWidth,
                          height: fieldHeight,
                          borderRadius: 2,
                          overflow: "hidden",
                          backgroundColor: fieldBg,
                          border: priceError ? "1px solid #d32f2f" : "none",
                        }}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label="Max price"
                          value={filters.priceMax}
                          onChange={(e) => {
                            if (e.target.value !== "" && !isPriceValue(e.target.value)) return;
                            const next = { ...filters, priceMax: e.target.value };
                            const maxVal = e.target.value;
                            const minVal = filters.priceMin;
                            if (minVal !== "" && maxVal !== "" && +minVal > +maxVal) {
                              setPriceError("Min price cannot be greater than max price");
                            } else {
                              setPriceError("");
                            }
                            setFilters(next);
                            filtersRef.current = next;
                            debouncedApplyPriceFilter();
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            margin: 0,
                            padding: "0 8px",
                            border: "none",
                            outline: "none",
                            backgroundColor: "transparent",
                            color: "#1a1a1a",
                            fontSize: "0.8125rem",
                            textAlign: "center",
                            boxSizing: "border-box",
                          }}
                        />
                        {!filters.priceMax && (
                          <Typography
                            component="span"
                            sx={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              pointerEvents: "none",
                              color: "#555555",
                              fontSize: "0.8125rem",
                            }}
                          >
                            Max
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    {priceError && (
                      <Typography variant="caption" color="error" mt={0.5} display="block">
                        {priceError}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md="auto">
                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                      Status
                    </Typography>
                    <FormControl size="small" sx={fieldSx}>
                      <Select
                        value={filters.status}
                        onChange={(e) => {
                          const next = { ...filters, status: e.target.value };
                          setFilters(next);
                          applyProductFilter(next);
                        }}
                        IconComponent={ExpandMoreIcon}
                      >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md="auto">
                  <Stack direction="row" alignItems="center" gap={2} sx={{ height: fieldHeight, alignItems: "center" }}>
                    <Typography
                      component="button"
                      type="button"
                      onClick={clearProductFilters}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 400,
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        textTransform: "none",
                        border: "none",
                        background: "none",
                        padding: 0,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      ClearAll
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            );
          })()}
        </Box>

       

        {canView && (
          <AgGridTable
            col={columnDefs}
            mainGridApiRef={mainGridApiRef}
            getRows={getRows}
            pageRef={pageRef}
            onRowClicked={onRowClicked}
            isLoading={loading}
            style={{ height: "56vh" }}
            gridOptions={{ rowHeight: 64 }}
          />
        )}
      </Box>
    </CommonLayout>
  );
}
